/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { MDXProvider, type MDXProviderComponentsProp } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';

import {
	HorizontalScrollContainer,
	Paragraph,
	SectionHeadingLink,
} from '@af/design-system-docs-ui';
import { Code, CodeBlock } from '@atlaskit/code';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ThemedImgMdx, type ThemedImgMdxProps } from '../../../components/themed-img';
import useSnippet, { type SnippetProps } from '../../../hooks/use-snippet';
import { TSMorphProps } from '../components/props';

interface ContentRendererProps {
	id: string;
	body: string;
	// It's an object with properties of `packageName` and value of object with
	// properties of `exportName` and values of the API data
	exportPropTypes?: Record<string, Record<string, any>>;
}

const hrStyles = css({ width: '100%' });
const blockquoteStyles = xcss({
	borderInlineStartColor: 'color.border',
	borderInlineStartWidth: 'border.width.indicator',
	borderInlineStartStyle: 'solid',
	paddingBlock: 'space.100',
	paddingInline: 'space.200',
	margin: 'space.0',
});

/**
 * Renders content for component pages.
 */
const ContentRenderer = ({ id, body, exportPropTypes }: ContentRendererProps) => {
	const components: MDXProviderComponentsProp = {
		/**
		 * `/components/<component_name>/examples` renders with a `<div hidden=""></div>`
		 * as the first element of the MDX content. This makes styling difficult, as we
		 * want to apply `margin-top: 0px;` to the first typographical element of every MDX
		 * document. By removing these hidden divs, we are able to simply apply:
		 * `:nth-child(1) { margin-top: 0px; }`
		 *
		 * This works, but ideally should be removed once we find out why hidden divs are
		 * being added to the content in the first place.
		 */
		div: ({ children, ...rest }) =>
			rest.hidden ? <Fragment></Fragment> : <div {...rest}>children</div>,
		hr: (props) => <hr role="presentation" {...props} css={hrStyles} />,
		h2: ({ id, children }) => (
			<SectionHeadingLink id={id} depth={2}>
				{children}
			</SectionHeadingLink>
		),
		h3: ({ id, children }) => (
			<SectionHeadingLink id={id} depth={3}>
				{children}
			</SectionHeadingLink>
		),
		p: ({ children }) => <Paragraph size="medium">{children}</Paragraph>,
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
		img: (props) => <img css={{ width: '100%' }} alt={props.alt || ''} {...props} />,
		ThemedImgMdx: ({ alt, className, id, height, width, children }: ThemedImgMdxProps) => {
			return (
				<ThemedImgMdx
					// Sets a default width of 100%
					css={{ width: '100%' }}
					alt={alt}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={className}
					id={id}
					height={height}
					width={width}
				>
					{children}
				</ThemedImgMdx>
			);
		},
		// TODO: Add `type` prop with default being component
		TSMorphProps: ({ exportName, packageName, filter }) => {
			return <TSMorphProps props={exportPropTypes?.[packageName]?.[exportName]} filter={filter} />;
		},
		code: ({ children, className, metastring }) => {
			/**
			 * Sets the 'language' prop.
			 * This is extracted from 'className' which is prefixed with 'language-'.
			 *
			 * @example
			 *  ```css
			 * .css-works-now {
			 * padding: 20;
			 * }
			 * ```
			 * @default 'tsx'
			 */
			const language = className !== undefined ? className.replace('language-', '') : 'tsx';

			/**
			 * Sets the 'showLineNumbers' prop.
			 * This is extracted from 'metastring'.
			 *
			 * @example
			 * ```text showLineNumbers=true
			 * Line 1
			 * Line 2
			 * Line 3
			 * ```
			 * @default false
			 */
			const showLineNumbers = metastring ? metastring.includes('showLineNumbers=true') : false;

			/**
			 * Sets the 'highlight' prop.
			 * This is extracted from 'metastring'.
			 *
			 * @example - Highlights line 1, 3 to 5
			 * ```text highlight=1,3-5
			 * 1 Yes
			 * 2 No
			 * 3 Yes
			 * 4 Yes
			 * 5 Yes
			 * ```
			 */
			const highlightMatches = metastring?.includes('highlight')
				? metastring.match(/highlight=(\S*)/)
				: false;
			const highlight = highlightMatches?.[1];

			// Remove additional line break before closing back ticks
			const text = children !== undefined ? children.replace(/\n$/g, '') : undefined;

			return (
				<CodeBlock
					text={text}
					language={language}
					showLineNumbers={showLineNumbers}
					highlight={highlight}
				/>
			);
		},
		inlineCode: ({ children }) => {
			return <Code>{children}</Code>;
		},
		table: ({ children }) => {
			return (
				<HorizontalScrollContainer>
					<table>{children}</table>
				</HorizontalScrollContainer>
			);
		},
		Snippet: ({ name, variables }: SnippetProps) => {
			const snippet = useSnippet(name, variables);
			return <ContentRenderer body={snippet} id={name} />;
		},
		quote: ({ children }) => {
			return (
				<Box as="blockquote" xcss={blockquoteStyles}>
					{children}
				</Box>
			);
		},
	};

	return (
		<MDXProvider components={components}>
			<MDXRenderer key={id}>{body}</MDXRenderer>
		</MDXProvider>
	);
};

/* eslint-disable @repo/internal/styles/consistent-style-ordering */
const wrapperStyles = css({
	/**
	 * Fixes a bug in FireFox where content overflows when it's too wide. If this
	 * is removed, please check that:
	 *  - /components/atlassian-navigation/examples
	 *  - /components/dynamic-table/examples
	 *  - /components/banner/examples
	 *
	 * display correctly in FireFox
	 */
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> p': {
		overflowWrap: 'break-word',
		margin: token('space.0'),
	},
});
/* eslint-enable @repo/internal/styles/consistent-style-ordering */

/**
 * __Content renderer wrapper__
 */
export const ContentRendererWrapper = ({ children }: { children: React.ReactNode }) => (
	<div css={wrapperStyles}>{children}</div>
);

export default ContentRenderer;
